import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { refreshTenantIdentity } from '../../shell/useTenantIdentity.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DisplaySettingsProvider } from '../../../../shared/hooks/useDisplaySettings.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { isNumberFormat } from '../../../../shared/contracts/business-number.js';
import { isBusinessDayFormat } from '../../../../shared/contracts/business-day.js';
import {
  getConfig,
  upsertConfig,
  deleteConfigOverride,
  type ConfigItem,
} from '../../api/configApi.js';

type TFn = (key: string, vars?: Record<string, string | number>) => string;

function checkRange(item: ConfigItem, n: number, t: TFn): string | null {
  if (item.allowedMin != null && n < Number(item.allowedMin))
    return t('config.edit.err.min', { min: String(item.allowedMin) });
  if (item.allowedMax != null && n > Number(item.allowedMax))
    return t('config.edit.err.max', { max: String(item.allowedMax) });
  return null;
}

/** Client-side mirror of the backend catalogue validation. */
function validateClient(item: ConfigItem, value: string, t: TFn): string | null {
  if (item.isSecret && value.trim() === '')
    return t('config.edit.err.secret');
  if (Array.isArray(item.allowedEnum) && item.allowedEnum.length > 0) {
    const ok = item.allowedEnum.some((e) => String(e) === value);
    if (!ok)
      return t('config.edit.err.enum', {
        values: item.allowedEnum.map((e) => String(e)).join(', '),
      });
  }
  if (item.allowedRegex) {
    let re: RegExp | null = null;
    try {
      re = new RegExp(item.allowedRegex);
    } catch {
      re = null;
    }
    if (re && !re.test(value)) return t('config.edit.err.pattern');
  }
  switch (item.valueType) {
    case 'INT':
      if (!/^-?\d+$/.test(value)) return t('config.edit.err.int');
      return checkRange(item, Number(value), t);
    case 'DECIMAL': {
      const n = Number(value);
      if (value.trim() === '' || !Number.isFinite(n))
        return t('config.edit.err.decimal');
      return checkRange(item, n, t);
    }
    case 'BOOLEAN':
      if (value !== 'true' && value !== 'false')
        return t('config.edit.err.boolean');
      return null;
    case 'JSON':
      try {
        JSON.parse(value);
      } catch {
        return t('config.edit.err.json');
      }
      return null;
    case 'DURATION':
      if (!item.allowedRegex && !/^\d+(ms|s|m|h|d)$/i.test(value))
        return t('config.edit.err.duration');
      return null;
    case 'BYTES':
      if (!item.allowedRegex && !/^\d+(\s?(B|KB|MB|GB|TB))?$/i.test(value))
        return t('config.edit.err.bytes');
      return null;
    default:
      return null;
  }
}

/** Illustrative figures for the preview only: they show the notation, not data. */
const SAMPLE_AMOUNT = 1234567.89;
const SAMPLE_QUANTITY = '1234.5';

/** Written by the same hook every screen uses, under the selected format. */
function NumberFormatSample(): ReactNode {
  const { t } = useTranslation();
  const { money, number, decimal } = useBusinessNumber();
  return (
    <dl>
      <dt>{t('config.preview.amount')}</dt>
      <dd data-testid="preview-amount">₹ {money(SAMPLE_AMOUNT)}</dd>
      <dt>{t('config.preview.quantity')}</dt>
      <dd data-testid="preview-quantity">{decimal(SAMPLE_QUANTITY)}</dd>
      <dt>{t('config.preview.groupSeparator')}</dt>
      <dd data-testid="preview-group">“{number(1000).charAt(1)}”</dd>
      <dt>{t('config.preview.decimalMark')}</dt>
      <dd data-testid="preview-decimal">“{number(1.5, 1).charAt(1)}”</dd>
    </dl>
  );
}

function DateFormatSample({ pattern }: { readonly pattern: string }): ReactNode {
  const { t } = useTranslation();
  const { day, at } = useBusinessDay();
  const now = useMemo(() => new Date(), []);
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return (
    <dl>
      <dt>{t('config.preview.pattern')}</dt>
      <dd data-testid="preview-pattern">{pattern}</dd>
      <dt>{t('config.preview.today')}</dt>
      <dd data-testid="preview-day">{day(today)}</dd>
      <dt>{t('config.preview.now')}</dt>
      <dd data-testid="preview-instant">{at(now)}</dd>
    </dl>
  );
}

/**
 * What the selected display format means, before it is saved. Only the two
 * parameters that `/v1/me/tenant` hands to every screen have a preview.
 */
function FormatPreview({ parameterCode, value }: { readonly parameterCode: string; readonly value: string }): ReactNode {
  const { t } = useTranslation();
  let sample: ReactNode = null;
  if (parameterCode === 'DISPLAY_NUMBER_FORMAT' && isNumberFormat(value)) {
    sample = <DisplaySettingsProvider numberFormat={value}><NumberFormatSample /></DisplaySettingsProvider>;
  } else if (parameterCode === 'DISPLAY_DATE_FORMAT' && isBusinessDayFormat(value)) {
    sample = <DisplaySettingsProvider dateFormat={value}><DateFormatSample pattern={value} /></DisplaySettingsProvider>;
  }
  if (!sample) return null;
  return (
    <section aria-label="format-preview">
      <h2>{t('config.preview.title')}</h2>
      {sample}
      <p>{t('config.preview.note')}</p>
    </section>
  );
}

export function ConfigEditPage(): ReactNode {
  const { t } = useTranslation();
  const { key } = useParams<{ key: string }>();
  const { token } = useAuth();
  const [item, setItem] = useState<ConfigItem | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    getConfig(key ?? '', token)
      .then((r) => {
        setItem(r);
        setValue(r.isSecret ? '' : r.value ?? '');
      })
      .catch((e) => setError((e as Error).message));

  useEffect(() => {
    if (!key) return;
    let alive = true;
    getConfig(key, token)
      .then((r) => {
        if (!alive) return;
        setItem(r);
        setValue(r.isSecret ? '' : r.value ?? '');
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [key, token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!item) return;
    setError(null);
    setStatus(null);
    const ve = validateClient(item, value, t);
    if (ve) {
      setFieldError(ve);
      return;
    }
    setFieldError(null);
    setSaving(true);
    try {
      const saved = await upsertConfig(item.parameterCode, value, token);
      refreshTenantIdentity();
      // The upsert echoes only the value fields; merging keeps the type and
      // range metadata that validateClient needs on the next edit.
      setItem({
        ...item,
        value: saved.value,
        hasOverride: saved.hasOverride,
        isSecret: saved.isSecret,
      });
      setValue(saved.isSecret ? '' : saved.value ?? '');
      setStatus(t('config.edit.saved'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onReset() {
    if (!item) return;
    setError(null);
    setStatus(null);
    try {
      await deleteConfigOverride(item.parameterCode, token);
      refreshTenantIdentity();
      setStatus(t('config.edit.overrideRemoved'));
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (error && !item) return <p role="alert">{error}</p>;
  if (!item) return <p>{t('config.edit.loading')}</p>;

  const hasEnum =
    Array.isArray(item.allowedEnum) && item.allowedEnum.length > 0;

  return (
    <main aria-label="config-edit">
      <h1>{item.parameterCode}</h1>
      <p data-testid="config-description">{item.description}</p>
      <dl>
        <dt>{t('config.edit.type')}</dt>
        <dd data-testid="config-type">{item.valueType}</dd>
        <dt>{t('config.edit.default')}</dt>
        <dd data-testid="config-default">{item.defaultValueText ?? '—'}</dd>
        {item.allowedMin != null || item.allowedMax != null ? (
          <>
            <dt>{t('config.edit.allowedRange')}</dt>
            <dd data-testid="config-range">
              {item.allowedMin ?? '−∞'} … {item.allowedMax ?? '∞'}
            </dd>
          </>
        ) : null}
        {item.allowedRegex ? (
          <>
            <dt>{t('config.edit.pattern')}</dt>
            <dd data-testid="config-regex">{item.allowedRegex}</dd>
          </>
        ) : null}
        {item.isSecret ? (
          <>
            <dt>{t('config.edit.sensitivity')}</dt>
            <dd data-testid="config-secret">{t('config.edit.secret')}</dd>
          </>
        ) : null}
      </dl>

      <form onSubmit={onSubmit} aria-label="config-form" noValidate>
        <label htmlFor="c-value">{t('config.edit.value')}</label>
        {hasEnum ? (
          <select
            id="c-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="field-value"
          >
            <option value="">{t('config.edit.select')}</option>
            {item.allowedEnum!.map((e) => (
              <option key={String(e)} value={String(e)}>
                {String(e)}
              </option>
            ))}
          </select>
        ) : item.valueType === 'BOOLEAN' ? (
          <select
            id="c-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="field-value"
          >
            <option value="">{t('config.edit.select')}</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : item.valueType === 'JSON' ? (
          <textarea
            id="c-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="field-value"
            rows={5}
          />
        ) : (
          <input
            id="c-value"
            value={value}
            placeholder={item.isSecret ? '•••••••• (enter new value)' : ''}
            onChange={(e) => setValue(e.target.value)}
            data-testid="field-value"
          />
        )}
        {fieldError ? <span data-testid="err-value">{fieldError}</span> : null}
        {error ? <p role="alert">{error}</p> : null}
        {status ? <p role="status">{status}</p> : null}
        <button type="submit" data-testid="submit-config" disabled={saving}>
          {t('config.edit.save')}
        </button>
        {item.hasOverride ? (
          <button type="button" onClick={onReset} data-testid="reset-default">
            {t('config.edit.resetDefault')}
          </button>
        ) : null}
      </form>
      <FormatPreview parameterCode={item.parameterCode} value={value} />
    </main>
  );
}
